import { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getUserPoints,
  getRiders,
  createSessionPass,
  updateSessionPass,
  deleteSessionPass,
} from '../../Utils/apiServices';
import ConfirmDialog from '../../Components/ConfirmDialog';

const DEFAULT_CATEGORIES = ['Children', 'Junior', 'Open'];

const flattenCategoriesToRiders = (data) => {
  if (!data?.categories) return [];
  return data.categories.flatMap((cat) =>
    (cat.riders || []).map((rider) => ({
      ...rider,
      category: cat.category,
      category_total_points: cat.total_points,
    }))
  );
};

const SessionalPoints = () => {
  const [year, setYear] = useState(null);
  const [categories, setCategories] = useState([]);
  const [riders, setRiders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePassId, setDeletePassId] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [formData, setFormData] = useState({
    id: '',
    rider_id: '',
    point: '',
    category: '',
  });

  const categoryOptions = useMemo(() => {
    const fromApi = (categories || []).map((c) => c.category).filter(Boolean);
    return fromApi.length > 0 ? fromApi : DEFAULT_CATEGORIES;
  }, [categories]);

  const tableRows = useMemo(() => flattenCategoriesToRiders({ categories }), [categories]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return tableRows;
    return tableRows.filter(
      (row) =>
        (row.rider_name?.toLowerCase() || '').includes(term) ||
        (row.rider_id?.toString() || '').includes(term) ||
        (row.category?.toLowerCase() || '').includes(term) ||
        (row.age_group?.toLowerCase() || '').includes(term)
    );
  }, [tableRows, searchTerm]);

  const fetchPoints = async () => {
    setLoading(true);
    try {
      const response = await getUserPoints();
      if (response.data?.success) {
        const { year: y, categories: cats } = response.data.data || {};
        setYear(y);
        setCategories(cats || []);
      }
    } catch (error) {
      console.error('Error fetching user points:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load sessional points',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRiders = async () => {
    try {
      const response = await getRiders();
      setRiders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching riders:', error);
    }
  };

  useEffect(() => {
    fetchPoints();
    fetchRiders();
  }, []);

  const resetForm = () => {
    setFormData({ id: '', rider_id: '', point: '', category: '' });
    setIsEditing(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setFormData({
      id: '',
      rider_id: row.rider_id?.toString() || '',
      point: row.total_points?.toString() || '',
      category: row.category || row.age_group || '',
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.rider_id || formData.point === '' || !formData.category) {
      setSnackbar({
        open: true,
        message: 'Please select a rider, category, and enter points',
        severity: 'warning',
      });
      return;
    }

    if (isEditing && !formData.id) {
      setSnackbar({
        open: true,
        message: 'Session pass ID is required to update a record',
        severity: 'warning',
      });
      return;
    }

    try {
      const payload = {
        rider_id: formData.rider_id.toString(),
        point: Number(formData.point),
        category: formData.category,
      };

      if (isEditing) {
        const response = await updateSessionPass({
          ...payload,
          id: Number(formData.id),
        });
        if (response.data?.success) {
          setSnackbar({
            open: true,
            message: 'Session pass updated successfully',
            severity: 'success',
          });
          await fetchPoints();
          handleClose();
        }
      } else {
        const response = await createSessionPass(payload);
        if (response.data?.success) {
          const createdId = response.data?.data?.id;
          setSnackbar({
            open: true,
            message: createdId
              ? `Session pass created (ID: ${createdId})`
              : 'Session pass created successfully',
            severity: 'success',
          });
          await fetchPoints();
          handleClose();
        }
      }
    } catch (error) {
      console.error('Error saving session pass:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to save session pass',
        severity: 'error',
      });
    }
  };

  const openDeleteConfirm = () => {
    setDeletePassId('');
    setDeleteDialogOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeletePassId('');
  };

  const confirmDelete = async () => {
    if (!deletePassId) {
      setSnackbar({
        open: true,
        message: 'Please enter the session pass ID to delete',
        severity: 'warning',
      });
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await deleteSessionPass(deletePassId);
      if (response.data?.success) {
        setSnackbar({
          open: true,
          message: 'Session pass deleted successfully',
          severity: 'success',
        });
        await fetchPoints();
        setDeleteDialogOpen(false);
        setDeletePassId('');
      }
    } catch (error) {
      console.error('Error deleting session pass:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete session pass',
        severity: 'error',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f59e0b 0%, #eab308 50%, #84cc16 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(245,158,11,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>🏆 Sessional Points</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Rider rankings and session pass management</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{tableRows.length}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {year != null ? `Year ${year}` : 'Records'}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <TextField
            placeholder="Search riders, category, age group..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, maxWidth: { sm: 400 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleOpenCreate}
            startIcon={<AddIcon />}
            sx={{ flexShrink: 0 }}
          >
            Add Session Pass
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Showing {filteredRows.length} of {tableRows.length} records
          </Typography>
          {categories.map((cat) => (
            <Chip
              key={cat.category}
              label={`${cat.category}: ${cat.total_points ?? 0} pts`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          ))}
        </Stack>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Update Session Pass' : 'Create Session Pass'}</DialogTitle>
        <DialogContent>
          {isEditing && (
            <TextField
              name="id"
              label="Session Pass ID"
              fullWidth
              margin="dense"
              type="number"
              value={formData.id}
              onChange={handleFormChange}
              required
              helperText="Required for update — use the ID from when the pass was created"
            />
          )}
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="sessional-rider-label">Rider</InputLabel>
            <Select
              labelId="sessional-rider-label"
              name="rider_id"
              value={formData.rider_id}
              label="Rider"
              onChange={handleFormChange}
            >
              {riders.map((rider) => (
                <MenuItem key={rider.id} value={rider.id?.toString()}>
                  {rider.name} ({rider.user_id || rider.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="sessional-category-label">Category</InputLabel>
            <Select
              labelId="sessional-category-label"
              name="category"
              value={formData.category}
              label="Category"
              onChange={handleFormChange}
            >
              {categoryOptions.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            name="point"
            label="Points"
            type="number"
            fullWidth
            margin="dense"
            value={formData.point}
            onChange={handleFormChange}
            required
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete session pass"
        message={
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter the session pass record ID to delete. This cannot be undone.
            </Typography>
            <TextField
              label="Session Pass ID"
              type="number"
              fullWidth
              size="small"
              value={deletePassId}
              onChange={(e) => setDeletePassId(e.target.value)}
              autoFocus
            />
          </Box>
        }
        confirmLabel="Delete"
        onCancel={closeDeleteConfirm}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50',
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                },
              }}
            >
              <TableCell>Rider ID</TableCell>
              <TableCell>Rider Name</TableCell>
              <TableCell>DOB</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Age Group</TableCell>
              <TableCell align="right">Total Points</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  No riders with sessional points found
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row) => (
                <TableRow
                  key={`${row.rider_id}-${row.category}`}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 }, transition: 'background-color 0.15s' }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{row.rider_id}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{row.rider_name?.trim()}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{row.dob || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{row.age ?? '—'}</TableCell>
                  <TableCell>
                    <Chip label={row.category} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{row.age_group}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={row.total_points ?? 0}
                      size="small"
                      sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <IconButton
                        onClick={() => handleOpenEdit(row)}
                        color="primary"
                        size="small"
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                        aria-label="Edit session pass"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={openDeleteConfirm}
                        color="error"
                        size="small"
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                        aria-label="Delete session pass"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SessionalPoints;
